<template>
  <Layout>
    <b-row class="pb-4 pt-2" align="center">
      <b-col md="3">
        <Button
          location="title"
          type="white"
          class="mr-0 float-left"
          @click="handleCancel()"
        >
          Voltar
        </Button>
      </b-col>
      <b-col md="6">
        <h1 class="title">Nota Fiscal de Serviço</h1>
        <strong v-if="!nfse.generalInformation.production" class="color-red">
          Ambiente de Homologação - Sem Valor Fiscal
        </strong>
      </b-col>
      <b-col md="3" />
    </b-row>
    <GeneralInformation :disabled="disabled" ref="generalInformation" />
    <Receiver :disabled="disabled" ref="receiver" />
    <Services :disabled="disabled" ref="services" />
    <Taxes :disabled="disabled" ref="taxes" />
    <Payment :disabled="disabled" ref="payment" />
    <ModalEditService />
    <b-row class="margin-button-save" align="center">
      <b-col md="3" />
      <b-col md="6">
        <Button type="cancel" @click="handleCancel()">
          <Close class="mb-1" title="Cancelar" /> Cancelar
        </Button>
        <Button v-if="!disabled" type="success" @click="handleTransmit()"
          >Transmitir
        </Button>
      </b-col>
      <b-col md="3">
        <Button
          v-if="!disabled"
          type="white"
          location="title"
          class="mr-0 float-right"
          @click="handleSave()"
        >
          Salvar
        </Button>
      </b-col>
    </b-row>
  </Layout>
</template>

<script>
import Close from 'vue-material-design-icons/Close';
import { Layout } from '@/components';
import { Button } from '@/components/Form';
import { mapActions, mapState } from 'vuex';
import { getIdByLastSlash } from '@/reusable/getIdByLastSlash';
import {
  GeneralInformation,
  Receiver,
  Services,
  Taxes,
  Payment,
} from '../components/collapses';
import ModalEditService from '../components/modals/ModalEditService/ModalEditService.vue';
import { PAYMENT_NAMES } from '../../../../../enums/paymentsThatRequireFinanceAgent';
import { ClientMixin } from '../../../../../../Application';

export default {
  name: 'NFSeForm',
  components: {
    Layout,
    Button,
    GeneralInformation,
    Receiver,
    Services,
    Taxes,
    Payment,
    ModalEditService,
    Close,
  },
  mixins: [ClientMixin],
  data() {
    return {
      disabled: false,
      statusToDisable: [3, 4],
      collapsesToValidate: [
        'generalInformation',
        'receiver',
        'services',
        'taxes',
        'payment',
      ],
    };
  },
  async mounted() {
    this.setLoading(true);
    await this.getFiscalValidation();
    this.getClient();

    const id = getIdByLastSlash(this.$route.fullPath);

    if (!id) {
      if (this.fiscalValidation.invalid) {
        this.$router.push({ name: 'NFSe' });
      }

      await this.setConfiguration();
      await this.applyTaxesConfigurations();
      await this.applyEmissionConfigurations();
      return this.setLoading(false);
    }

    try {
      await this.setDetailedNfse(id);
    } catch (error) {
      this.$router.push({ name: 'NFSe' });
    }

    if (this.statusToDisable.includes(this.nfse.status)) {
      this.disabled = true;
    }

    if (this.fiscalValidation.invalid && !this.disabled) {
      this.$router.push({ name: 'NFSe' });
    }

    return this.setLoading(false);
  },
  computed: {
    ...mapState('nfse', ['nfse', 'fiscalValidation']),
  },
  methods: {
    ...mapActions('application', ['setLoading']),
    ...mapActions('nfseConfigurations', ['setConfiguration']),
    ...mapActions('nfse', [
      'registerNfse',
      'updateNfse',
      'transmitNfse',
      'setDetailedNfse',
      'applyTaxesConfigurations',
      'applyEmissionConfigurations',
      'hasInvalidCollapse',
      'getFiscalValidation',
    ]),

    setDefaultFields() {
      this.$refs.payment.setDefaultFields();
    },
    handleCancel() {
      return this.$router.push({ name: 'NFSe' });
    },
    async handleSave() {
      this.setDefaultFields();
      if (!(await this.isValidNfse())) return false;

      this.setLoading(true);

      await this.saveOrUpdate();

      this.$alert.success('NFS-e salva com sucesso!');

      this.$router.push({ name: 'NFSe' });

      return this.setLoading(false);
    },
    async saveOrUpdate() {
      let nfseId;
      const id = getIdByLastSlash(this.$route.fullPath);

      if (!id) {
        nfseId = await this.registerNfse(this.nfse);
      } else {
        nfseId = await this.updateNfse(this.nfse);
      }

      return nfseId;
    },
    async isValidNfse() {
      if (this.nfse.taxes.total <= 0) {
        return this.$alert.error('Total da nota deve ser maior que R$ 0,00');
      }

      if (!this.validate1601FinanceAgent()) {
        return this.$alert.error(
          `O parâmetro para gerar o registro 1601 do SPED está ativado e o parcelamento está sem 
          conta bancária ou a mesma está sem agente financeiro, verifique.`,
        );
      }

      this.$refs.generalInformation.$v.$touch();
      this.$refs.receiver.$v.$touch();
      this.$refs.services.$v.$touch();
      this.$refs.taxes.$v.$touch();
      this.$refs.payment.$v.$touch();

      const params = {
        collapsesToValidate: this.collapsesToValidate,
        refs: this.$refs,
        root: this.$root,
      };

      if (await this.hasInvalidCollapse(params)) return false;
      return true;
    },
    validate1601FinanceAgent() {
      const has1601ConfigurationEnabled =
        this.client.fiscal.emissor.enable1601Sped;

      return this.nfse.payments.paymentModeCollection.every((paymentMode) => {
        if (
          has1601ConfigurationEnabled &&
          !paymentMode.financeAgentId &&
          PAYMENT_NAMES.includes(paymentMode.paymentType.name)
        ) {
          return false;
        }

        return true;
      });
    },
    async handleTransmit() {
      this.setDefaultFields();
      if (!(await this.isValidNfse())) return false;

      this.setLoading(true);

      const nfseId = await this.saveOrUpdate();
      await this.transmitNfse(nfseId);

      this.$alert.success('NFS-e em processamento!');
      this.$router.push({ name: 'NFSe' });

      return this.setLoading(false);
    },
  },
};
</script>
